import { AccountConnection } from 'src/interfaces/accountConnection.interface';
import { AzureVoice, Voice } from 'src/interfaces/voice.interface';
import { post } from './requests';

declare global {
  interface Window {
    webkitAudioContext: typeof AudioContext;
  }
}

const playTTS = async (
  voice: Voice,
  text: string,
  envId: string,
  provider?: AccountConnection,
  isProviderConnected?: boolean,
) => {
  try {
    switch (voice.type) {
      case 'voice_azure': {
        const body = `<speak version='1.0' xml:lang='en-US'>
              <voice xml:lang='en-US' name='${voice.voice_name}'>
                <prosody rate='${(voice as AzureVoice).rate || 0}%' pitch='${(voice as AzureVoice).pitch || 0}%'>
                  ${text}
                </prosody>
              </voice>
            </speak>`;

        if (isProviderConnected && provider) {
          const response = await fetch(
            `https://${provider.credentials.region}.tts.speech.microsoft.com/cognitiveservices/v1`,
            {
              method: 'POST',
              headers: {
                'Ocp-Apim-Subscription-Key': provider.credentials.api_key,
                'Content-Type': 'application/ssml+xml',
                'X-Microsoft-OutputFormat': 'audio-16khz-128kbitrate-mono-mp3',
              },
              body,
            },
          );

          const arrayBuffer = await response.arrayBuffer();
          return playArrayBuffer(arrayBuffer);
        }

        const responseAzure = await post(
          `/voices/global/listen`,
          {
            headers: {
              'Content-Type': 'application/ssml+xml',
              'X-Microsoft-OutputFormat': 'audio-16khz-128kbitrate-mono-mp3',
            },
            body,
            type: 'voice_azure',
          },
          { envId },
        );

        return playArrayBuffer(responseAzure.data as ArrayBuffer);
      }

      case 'voice_eleven_labs': {
        if (isProviderConnected && provider) {
          const response = await fetch(
            `https://api.elevenlabs.io/v1/text-to-speech/${voice.voice_id}`,
            {
              method: 'POST',
              headers: {
                'xi-api-key': provider.credentials.api_key,
                'Content-Type': 'application/json',
              },
              body: JSON.stringify({
                text,
                model_id: voice.model_id,
                stability: voice.stability,
                similarity_boost: voice.similarity_boost,
              }),
            },
          );

          const arrayBuffer = await response.arrayBuffer();
          return playArrayBuffer(arrayBuffer);
        }

        const responseElevenLabs = await post(
          `/voices/global/listen`,
          {
            type: 'voice_eleven_labs',
            voice_id: voice.voice_id,
            text,
            model_id: voice.model_id,
            stability: voice.stability,
            similarity_boost: voice.similarity_boost,
          },
          { envId },
        );

        return playArrayBuffer(responseElevenLabs.data as ArrayBuffer);
      }

      case 'voice_cartesia': {
        const bodyToListen = {
          model_id: voice.model || 'sonic-english',
          transcript: text,
          voice: {
            mode: 'id',
            id: voice.voice_id,
          },
          output_format: {
            container: 'mp3',
            bit_rate: 128000,
            sample_rate: 44100,
          },
          language: 'en',
        };

        if (isProviderConnected && provider) {
          const response = await fetch('https://api.cartesia.ai/tts/bytes', {
            method: 'POST',
            headers: {
              'Cartesia-Version': '2024-06-10',
              'X-API-Key': provider.credentials.api_key,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(bodyToListen),
          });

          const arrayBuffer = await response.arrayBuffer();
          return playArrayBuffer(arrayBuffer);
        }

        const responseCartesia = await post(
          `/voices/global/listen`,
          {
            type: 'voice_cartesia',
            body: bodyToListen,
          },
          { envId },
        );

        return playArrayBuffer(responseCartesia.data as ArrayBuffer);
      }

      case 'voice_rime': {
        const bodyToListen = {
          text,
          speaker: voice.speaker,
          model_id: voice.model_id,
          speed_alpha: voice.speed_alpha,
        };

        if (isProviderConnected && provider) {
          const response = await fetch('https://users.rime.ai/v1/rime-tts', {
            method: 'POST',
            headers: {
              accept: 'audio/mpeg',
              Authorization: `Bearer ${provider.credentials.api_key}`,
              'Content-Type': 'application/json',
            },
            body: JSON.stringify(bodyToListen),
          });

          const base64Audio = await response.json();
          const audioContent = base64Audio.audioContent;
          const binaryString = atob(audioContent);
          const bytes = new Uint8Array(binaryString.length);
          for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          return playArrayBuffer(bytes.buffer);
        }

        const responseRime = await post(
          '/voices/global/listen',
          {
            type: 'voice_rime',
            body: bodyToListen,
          },
          {
            envId,
          },
        );

        return playArrayBuffer(responseRime.data as ArrayBuffer);
      }

      case 'voice_play_ht': {
        const bodyToListen = {
          text,
          voice: voice.voice_id,
          output_format: 'mp3',
          ...(voice.version && { engine: voice.version }),
          ...(voice.speed && { speed: voice.speed }),
        };

        if (isProviderConnected && provider) {
          const response = await fetch(
            'https://api.play.ht/api/v2/tts/stream',
            {
              method: 'POST',
              headers: {
                accept: 'audio/mpeg',
                'content-type': 'application/json',
                AUTHORIZATION: `Bearer ${provider.credentials.api_key}`,
                'X-USER-ID': provider.credentials.userId,
              },
              body: JSON.stringify(bodyToListen),
            },
          );

          const arrayBuffer = await response.arrayBuffer();
          return playArrayBuffer(arrayBuffer);
        }

        const responsePlayHT = await post(
          '/voices/global/listen',
          {
            type: 'voice_playht',
            body: bodyToListen,
            ...(isProviderConnected && provider
              ? {
                  api_key: provider.credentials.api_key,
                  user_id: provider.credentials.userId,
                }
              : {}),
          },
          { envId },
        );

        return playArrayBuffer(responsePlayHT.data as ArrayBuffer);
      }

      default:
        throw new Error('Unsupported voice type');
    }
  } catch (error) {
    console.error('TTS Error:', error);
    throw error;
  }
};

const playArrayBuffer = async (arrayBuffer: ArrayBuffer) => {
  const uint8Array = new Uint8Array(arrayBuffer);
  const audioBlob = new Blob([uint8Array], {
    type: 'audio/mpeg',
  });
  const audioUrl = URL.createObjectURL(audioBlob);
  const audio = new Audio(audioUrl);

  audio.addEventListener('ended', () => {
    URL.revokeObjectURL(audioUrl);
  });

  await audio.play();

  return true;
};

export const listenVoice = async (
  voice: Voice,
  text: string,
  envId: string,
  provider?: AccountConnection,
  isProviderConnected?: boolean,
) => {
  return await playTTS(voice, text, envId, provider, isProviderConnected);
};
